import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  RefObject
} from "react";
import { useSelector } from "react-redux";
import { isUndefined } from "lodash";
import mediator from "@tvg/mediator";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import {
  getIsVerified,
  getIsTermsAccepted,
  getIsLogged
} from "@urp/store-selectors/users";
import { getHasCrossNavigation } from "../redux/selectors";
import { Section, ComponentOrder } from "../types";

interface Props {
  enablePillsNav: boolean;
  componentsOrder: ComponentOrder[];
}

const LOADING_TIMEOUT_DELAY = 3000;
const PILLS_HEIGHT = 48;
const HEADER_HEIGHT = 48;
const CROSS_NAV_HEIGHT = 58;
const NON_COMPLIANT_USER_BANNER = 34;
let isScrollingThroughPills = false;

export const handleSectionClick = (
  sectionName: string,
  element: HTMLElement | null,
  setSelectedSection: Dispatch<SetStateAction<string>>,
  pillsDistanceFromTop: number
) => {
  if (window && element) {
    const yOffset = pillsDistanceFromTop + PILLS_HEIGHT;
    const elementDistanceToTop =
      window.scrollY + (element?.getBoundingClientRect().top ?? 0);
    const y = elementDistanceToTop - yOffset;

    isScrollingThroughPills = true;
    window.scrollTo({ top: y, behavior: "smooth" });

    setTimeout(() => {
      isScrollingThroughPills = false;
    }, 1000);
  }

  setSelectedSection(sectionName);
};

const sendAnalyticEvt = (sectionTitle: string) => {
  mediator.base.dispatch({
    type: "HEADER_PILL_CLICKED",
    payload: {
      eventLabel: sectionTitle
    }
  });
};

const useSections = ({ enablePillsNav, componentsOrder }: Props) => {
  let observer = undefined as IntersectionObserver | undefined;
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [sections, setSections] = useState<Section[]>([]);
  const [pillsDistanceFromTop, setPillsDistanceFromTop] = useState(48);

  const hasCrossNavigation = useSelector(getHasCrossNavigation);
  const userIsVerified = useSelector(getIsVerified);
  const userHasAcceptedTerms = useSelector(getIsTermsAccepted);
  const userIsLogged = useSelector(getIsLogged);

  const hasUserUnverifiedBanner =
    !isXSell() &&
    userIsLogged &&
    ((!isUndefined(userIsVerified) && !userIsVerified) ||
      (!isUndefined(userHasAcceptedTerms) && !userHasAcceptedTerms));

  const sortOrder = (a: Section, b: Section) => {
    const indexA = componentsOrder.findIndex((item) => item.name === a.name);
    const indexB = componentsOrder.findIndex((item) => item.name === b.name);
    return indexA - indexB;
  };

  const pills = sections.map(({ name, title, element }) => ({
    title,
    name,
    isActive: selectedSection === name,
    onClick: () => {
      handleSectionClick(
        name,
        element,
        setSelectedSection,
        pillsDistanceFromTop
      );
      sendAnalyticEvt(title);
    }
  }));

  useEffect(() => {
    setPillsDistanceFromTop(
      HEADER_HEIGHT +
        (hasCrossNavigation ? CROSS_NAV_HEIGHT : 0) +
        (hasUserUnverifiedBanner ? NON_COMPLIANT_USER_BANNER : 0)
    );
  }, [hasUserUnverifiedBanner, hasCrossNavigation]);

  useEffect(() => {
    if (sections?.length > 0 && !isLoading) {
      const [firstSection] = sections;
      setSelectedSection(firstSection.name);
    }
  }, [sections, isLoading]);

  // Since we're inferring which sections are on the DOM based on a Mutation Observer (ComponentSection),
  // there's no way to know exactly when all the sections are available/present on the DOM, which is why there's a timeout.
  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, LOADING_TIMEOUT_DELAY);
    return () => clearTimeout(loadingTimeout);
  }, []);

  useEffect(() => {
    if (sections.length > 1) {
      const windowInnerHeight = window && window.innerHeight;
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!isScrollingThroughPills) {
              let extractedTab;
              if (
                !entry.isIntersecting &&
                entry.boundingClientRect.bottom < windowInnerHeight
              ) {
                extractedTab =
                  sections[
                    sections.findIndex(
                      ({ element }) => element === entry.target
                    ) + 1
                  ];
              }
              if (
                entry.isIntersecting &&
                // 170 = headers + pills + title container of each section
                entry.boundingClientRect.top < 170 &&
                !isScrollingThroughPills
              ) {
                extractedTab =
                  sections[
                    sections.findIndex(
                      ({ element }) => element === entry.target
                    )
                  ];
              }

              if (extractedTab?.name) {
                setSelectedSection(extractedTab.name);
              }
            }
          });
        },
        { threshold: 0, rootMargin: "-170px" }
      );

      sections.forEach(
        ({ element }) => element && observer && observer.observe(element)
      );
    }

    return () => {
      sections.forEach(
        ({ element }) => element && observer && observer.unobserve(element)
      );
    };
  }, [sections]);

  const handleOnSectionVisibilityChanges = (
    name: string,
    title: string = "",
    sectionWrapperRef: RefObject<HTMLElement>,
    isComponentVisible: boolean
  ) => {
    if (!enablePillsNav) {
      return null;
    }
    // Check if a section is already created to prevent duplications
    const isSectionAlreadyCreated = (secs: Section[]) =>
      secs.some((sec) => sec.name === name);

    // Criteria to filter the Sections object if an HTML section is removed from DOM
    const filterCriteria = (sec: Section) => sec.name !== name;

    if (isComponentVisible) {
      setSections((prev: Section[]) => {
        if (!isSectionAlreadyCreated(prev)) {
          return [
            ...prev,
            { element: sectionWrapperRef.current, name, title }
          ].sort(sortOrder);
        }
        return prev;
      });
    } else {
      setSections((prev) => prev.filter(filterCriteria));
    }

    return undefined;
  };

  return {
    pills,
    handleOnSectionVisibilityChanges,
    isLoading,
    sections,
    hasCrossNavigation,
    hasUserUnverifiedBanner,
    pillsDistanceFromTop
  };
};

export default useSections;
